import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { CreditCard, Copy, CheckCircle2, Send } from 'lucide-react';
import { sendPaymentRequest, cancelPaymentRequest, listPaymentRequests, sendPaymentRequestAgain, getDealTransactions, getApplicableCredits, getDeferredCollections, endDeferral } from '@/services/deals.service.js';
import { api, ApiError } from '@/lib/api.js';
import { formatDateOrEmpty } from '@/lib/date-display.js';
import { resolveLine } from '@/lib/insurance-line.js';
import { isObligationSettled } from '@/lib/arStatus.js';
import { getPaymentHolds, isHoldRefusal } from '@/services/paperwork.service.js';
import { SignedApplicationHold, EndorsementDocumentHold, UnmatchedEndorsementHold } from '@/components/paperwork/PaymentHoldBlock.jsx';
import PolicyDocsDropSheet from '@/components/sheets/PolicyDocsDropSheet.jsx';

// An unpaid executed endorsement txn flips the sheet into endorsement mode —
// the same condition the server uses to derive payment_request_kind.
function findUnpaidEndorsementTxn(transactions) {
  return (transactions || []).find(
    (t) =>
      t.type === 'endorsement' &&
      t.status === 'executed' &&
      !t.voided_at &&
      !isObligationSettled(t.ar_payment_status)
  ) || null;
}

// Canon (185). The T12:00:00 anchor is no longer needed: the formatter
// splits a bare YYYY-MM-DD rather than letting new Date read it as UTC.
const fmtD = (s) => formatDateOrEmpty(s, { month: 'short', day: 'numeric', year: 'numeric' }) || s || '';

const money2 = (n) => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const SEND_WORD = { initial: 'Sent', reminder: 'reminded', resend: 'resent' };

// "Sent Oct 6 to a@b.com, reminded Oct 22". A request made before the history
// existed recorded no sends, and says so rather than looking unsent.
function sendHistory(req) {
  const sends = req.sends || [];
  if (!sends.length) return `Created ${fmtD(String(req.created_at || '').slice(0, 10))}, no send recorded`;
  return sends.map((s, i) => {
    const word = i === 0 ? (s.kind === 'initial' ? 'Sent' : SEND_WORD[s.kind] || 'Sent') : (SEND_WORD[s.kind] || 'sent');
    const cap = i === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word;
    return `${cap} ${fmtD(String(s.at || '').slice(0, 10))}${s.to ? ` to ${s.to}` : ''}`;
  }).join(', ');
}

// The deal's open requests, listed before the create form (spec §1): copy the
// link, send a reminder, resend to another address, cancel. Every button is
// the broker's own click; nothing here schedules anything.
function OpenRequests({ requests, failed, dealId, onSent, onCancelled }) {
  const [busy, setBusy] = useState(null);
  const [altFor, setAltFor] = useState(null);
  const [altEmail, setAltEmail] = useState('');
  const [cancelling, setCancelling] = useState(false);
  if (failed) {
    return (
      <p className="my-2 text-xs text-danger">
        Could not check this deal for open payment requests. Reopen to retry before sending a new one.
      </p>
    );
  }
  if (!requests.length) return null;

  async function send(req, kind, toEmail) {
    setBusy(`${req.session_id}:${kind}`);
    try {
      const res = await sendPaymentRequestAgain(req.session_id, { kind, toEmail });
      if (!res?.success) throw new Error(res?.error || 'Send failed');
      toast.success(`${kind === 'reminder' ? 'Reminder' : 'Request'} sent to ${res.to}`);
      // The email left; the history may not have been written. Say so.
      if (res.record_error) toast.info(`Sent, but the send history was not recorded: ${res.record_error}`);
      setAltFor(null); setAltEmail('');
      onSent?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Send failed');
    } finally {
      setBusy(null);
    }
  }

  async function cancelAll() {
    setCancelling(true);
    try {
      const res = await cancelPaymentRequest(dealId);
      if (!res?.success) throw new Error(res?.error || 'Cancel failed');
      toast.success(requests.length > 1 ? 'Open payment requests cancelled' : 'Payment request cancelled');
      onCancelled?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Cancel failed');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="my-2 space-y-2">
      <p className="text-xs font-medium text-text-dim">
        {requests.length === 1 ? 'Open payment request on this deal' : `${requests.length} open payment requests on this deal`}
      </p>
      {requests.map((req) => (
        <div key={req.session_id} className="rounded-card border border-border bg-bg p-3 space-y-2">
          <div className="flex items-baseline justify-between gap-2 text-sm">
            <span className="text-text"><strong>{req.total == null ? '' : money2(req.total)}</strong> {req.kind === 'endorsement' ? 'endorsement' : ''}{req.carrier_name ? ` · ${req.carrier_name}` : ''}</span>
            <span className="text-xs text-text-muted">{req.acknowledged || req.status === 'submitted' ? 'Acknowledged, not paid' : 'Not paid'}</span>
          </div>
          <p className="text-xs text-text-muted">{sendHistory(req)}</p>
          {req.obligation_settled === true && (
            <p className="text-xs text-warning">
              The ledger shows this {req.obligation_status === 'voided' ? 'transaction voided' : `${req.obligation_status === 'succeeded' ? 'paid' : req.obligation_status}`}, but the request is still open. Cancel it rather than reminding.
            </p>
          )}
          {req.obligation_check_failed && (
            <p className="text-xs text-danger">Could not check the ledger for this request. Reopen before reminding.</p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(req.access_url).then(() => toast.success('Link copied')).catch(() => toast.error('Copy failed'))}>
              <Copy size={14} strokeWidth={1.5} /> Copy link
            </Button>
            <Button size="sm" disabled={!!busy || !req.contact_email || req.obligation_settled === true || req.obligation_check_failed} onClick={() => send(req, 'reminder')} title={req.contact_email ? `Remind ${req.contact_email}` : 'No email on file; send to another address'}>
              <Send size={14} strokeWidth={1.5} /> {busy === `${req.session_id}:reminder` ? 'Sending...' : 'Send reminder'}
            </Button>
            <Button size="sm" variant="ghost" disabled={!!busy || req.obligation_settled === true || req.obligation_check_failed} onClick={() => { setAltFor(altFor === req.session_id ? null : req.session_id); setAltEmail(''); }}>
              Send to another address
            </Button>
          </div>
          {altFor === req.session_id && (
            <div className="flex gap-2">
              <Input type="email" value={altEmail} onChange={(e) => setAltEmail(e.target.value)} placeholder="name@example.com" />
              <Button size="sm" disabled={!!busy || !altEmail.trim()} onClick={() => send(req, 'resend', altEmail.trim())}>
                {busy === `${req.session_id}:resend` ? 'Sending...' : 'Send'}
              </Button>
            </div>
          )}
        </div>
      ))}
      <Button size="sm" variant="ghost" className="text-text-muted" disabled={cancelling} onClick={cancelAll}>
        {cancelling ? 'Cancelling...' : (requests.length > 1 ? 'Cancel all open requests' : 'Cancel this request')}
      </Button>
    </div>
  );
}

// `prefill` (optional): { whatYourePayingFor, changesSummary } — set by the
// no-app renewal bind flow (cockpit Bind dialog checkbox) so the summary of
// changes the broker wrote lands in the request description and, via
// changes_summary, in the client acknowledgment text. Renewal mode only.
export default function PaymentRequestSheet({ open, onOpenChange, dealId, onChange, prefill = null }) {
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(false);

  const [premium, setPremium] = useState('');
  const [estimatedTaxes, setEstimatedTaxes] = useState('0');
  const [effective, setEffective] = useState('');
  const [expiration, setExpiration] = useState('');
  const [policyNumber, setPolicyNumber] = useState('');
  const [whatYourePayingFor, setWhatYourePayingFor] = useState('');
  const [brokerMessage, setBrokerMessage] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  // The default subject names the total, so it follows the total (fees, taxes,
  // deferred lines, credit) until David types over it; then it is his.
  const subjectTpl = useRef(null);
  const autoSubject = useRef('');
  const setAutoSubject = (tpl, amount) => {
    subjectTpl.current = tpl;
    const next = tpl ? tpl(amount) : '';
    autoSubject.current = next;
    setEmailSubject(next);
  };
  const [carrierMissing, setCarrierMissing] = useState(false);
  // Guard (a): the server's answer for a deal with no live bind ({ live, chosen, candidates, ask, none, why } or { failed }).
  const [bindPlan, setBindPlan] = useState(null);
  const [bindQuoteId, setBindQuoteId] = useState('');
  // The carrier's program fee this request bills (the server's carrierFeeToBill, the same answer /initiate charges).
  // null = the ledger read failed: the preview says so and Send waits, as with unreadable fees.
  const [carrierFeeDue, setCarrierFeeDue] = useState(0);
  // The deal's open requests (PAYMENT-REQUEST-MANAGER-SPEC §1). A failed read
  // says so; it never renders as "no open request".
  const [openRequests, setOpenRequests] = useState([]);
  const [openFailed, setOpenFailed] = useState(false);
  const [reloadOpen, setReloadOpen] = useState(0);
  const [endorsementTxn, setEndorsementTxn] = useState(null);
  // The unmatched endorsement hold's named fix (review fix, 2026-10-31): "Collect the endorsement instead" opens this sheet
  // on that endorsement (the loader takes it by id), never a renewal request beside it.
  const [collectTxnId, setCollectTxnId] = useState(null);
  // An endorsement request states its own fees (David 2026-10-28, option c): the
  // platform fee (blank = standard; a reason when below it) and an agency fee
  // (blank = none). The server checks them through utils/platform-fee parseStatedFees.
  const [endoPlatform, setEndoPlatform] = useState('');
  const [endoReason, setEndoReason] = useState('');
  const [endoAgency, setEndoAgency] = useState('');
  // Deferred items riding this renewal (184 §4). Fetched from the server so the
  // AMOUNTS shown are the server's own, never re-derived here: the server
  // recomputes the total from the same source and the same exclusion list, so
  // the number David sees is the number that will be charged.
  const [deferredItems, setDeferredItems] = useState([]);
  // A FAILED LOOKUP AND AN ACCOUNT THAT OWES NOTHING ARE NOT THE SAME THING.
  // This used to .catch(() => setDeferredItems([])), so a network blip rendered
  // exactly like a clean account and David would have sent an invoice missing
  // money without a single sign that anything had gone wrong.
  const [deferredFailed, setDeferredFailed] = useState(false);
  const [deferredSentence, setDeferredSentence] = useState(null);
  const [excludedDeferred, setExcludedDeferred] = useState([]);
  // Bumped after a deferral on this deal is ended, so the ledger and the
  // deferred listing are read again and the sheet invoices what the store holds.
  const [reloadKey, setReloadKey] = useState(0);
  const [endingDeferral, setEndingDeferral] = useState(null);
  // Applied credit (REWRITE-AND-CREDITS-SPEC §B4, amended 2026-09-30). The
  // picker offers ONLY credits a trigger admits on this deal: the deal's own
  // account, or a rewrite of the credit's source policy. The server decides
  // (assertApplicable); this list is its mirror. Not offered in endorsement
  // mode (server rejects it).
  const [availableCredits, setAvailableCredits] = useState([]);
  const [selectedCreditId, setSelectedCreditId] = useState('');
  // THE PAPERWORK HOLDS (paperwork-1 D9, D11, L2-2): the server's preview (GET /deals/:dealId/payment-holds), read on open and
  // after anything that could change it (a drop, a waiver, an attach, a refused send). A failed read says so; the server still
  // decides at send. `refusal` is a 409 hold's body, shown with its button.
  const [holds, setHolds] = useState(null);
  const [holdsFailed, setHoldsFailed] = useState(false);
  const [reloadHolds, setReloadHolds] = useState(0);
  const [refusal, setRefusal] = useState(null);
  const [appDropOpen, setAppDropOpen] = useState(false);
  useEffect(() => {
    if (!open || !dealId) { setHolds(null); setHoldsFailed(false); setRefusal(null); return; }
    let cancelled = false;
    getPaymentHolds(dealId)
      .then((r) => { if (!cancelled) { setHolds(r || null); setHoldsFailed(false); } })
      .catch(() => { if (!cancelled) { setHolds(null); setHoldsFailed(true); } });
    return () => { cancelled = true; };
  }, [open, dealId, reloadHolds]);
  const holdsChanged = () => { setRefusal(null); setReloadHolds((k) => k + 1); };
  const collectEndorsement = (txnId) => { setCollectTxnId(txnId); setRefusal(null); setReloadHolds((k) => k + 1); setReloadKey((k) => k + 1); };
  // What the create would hold now, as the server's preview says it (would_hold); a refusal holds until something changes.
  const previewFindings = !holds ? [] : endorsementTxn ? (holds.payment_request?.endorsement?.[endorsementTxn.id] || []) : (holds.payment_request?.renewal || []);
  const heldNow = !!refusal || previewFindings.some((f) => f.would_hold);
  // Guard (a): with no live bind, the quote the send records it from (the server's plan), and whether the sheet must ask.
  const bindQuote = bindPlan && !bindPlan.live ? (bindPlan.candidates || []).find((c) => String(c.quote_id) === String(bindQuoteId)) || null : null;
  const bindWillRecord = !endorsementTxn && !!bindPlan && !bindPlan.failed && !bindPlan.live && !!bindQuote;
  const bindNeedsChoice = !endorsementTxn && !!bindPlan && !bindPlan.failed && !bindPlan.live && !bindQuote;
  const carrierBlocks = carrierMissing && !(bindQuote && bindQuote.carrier);
  // A refusal answered the request as it was; a changed amount or mode is another request, which the server answers afresh.
  useEffect(() => { setRefusal(null); }, [premium, endorsementTxn]);

  useEffect(() => {
    if (!open || !dealId) { setSelectedCreditId(''); return; }
    getApplicableCredits(dealId)
      .then((r) => setAvailableCredits(r?.credits || []))
      .catch(() => setAvailableCredits([]));
  }, [open, dealId]);

  useEffect(() => {
    if (!open || !dealId) { setOpenRequests([]); setOpenFailed(false); return; }
    let cancelled = false;
    setOpenFailed(false);
    listPaymentRequests(dealId)
      .then((r) => { if (!cancelled) setOpenRequests(r?.requests || []); })
      .catch(() => { if (!cancelled) { setOpenRequests([]); setOpenFailed(true); } });
    return () => { cancelled = true; };
  }, [open, dealId, reloadOpen]);

  useEffect(() => {
    if (!open || !dealId) return;
    setResult(null);
    setLoading(true);
    setCarrierMissing(false);
    setBindPlan(null);
    setBindQuoteId('');
    setEndorsementTxn(null);
    setEndoPlatform(''); setEndoReason(''); setEndoAgency('');
    Promise.all([
      api.get(`/api/tracon/deal/${encodeURIComponent(dealId)}`),
      // Ledger check is best-effort — the server re-derives kind regardless.
      getDealTransactions(dealId).catch(() => null),
    ])
      .then(([res, txnRes]) => {
        const d = res?.deal || {};
        setDeal(d);
        setPolicyNumber(d.policy_number || '');
        const carrier = d.carrier || '';
        const start = d.policy_start ? d.policy_start.split('T')[0] : '';
        const end = d.policy_expiration ? d.policy_expiration.split('T')[0] : '';
        if (!carrier) setCarrierMissing(true);
        // The default description names the policy by its line: a hangar renewal
        // is not an aviation policy (Positive Rate, 2026-09-25), and a builders
        // risk policy is not a hangar and premises policy (LH Construction
        // ND-APP-00343, 2026-10-01). `d` is the pipeline item, whose
        // Insurance_Line is already canonical and which carries Builders_Risk_Data.
        const line = resolveLine(d);
        const lineNoun = line.isBuildersRisk ? 'builders risk' : (line.isProperty ? 'hangar and premises' : 'aviation');
        // Renewal is a fact on the deal, not an assumption of this sheet: a new
        // policy was being described as "Renewal of your ... Same coverage as the
        // expiring policy" (LH Construction, 2026-10-01). The server derives
        // policy_kind once (utils/payment-request-text paymentRequestKind) and
        // the invite email reads the same answer; this renders it.
        const kind = d.policy_kind || 'renewal';
        const isRenewal = kind === 'renewal';
        // Builders risk: the project, from the deal's BR data (a JSON string on
        // the pipeline item), so the client reads what the policy is for.
        let brProject = '';
        if (line.isBuildersRisk && d.Builders_Risk_Data) {
          try {
            const brd = typeof d.Builders_Risk_Data === 'string' ? JSON.parse(d.Builders_Risk_Data) : d.Builders_Risk_Data;
            brProject = String(brd?.project?.description || '').trim();
          } catch (_) { brProject = ''; }
        }

        setCarrierFeeDue(txnRes ? (Number(txnRes.carrier_fee_to_bill) || 0) : null);
        const endo = collectTxnId ? (txnRes?.transactions || []).find((t) => String(t.id) === String(collectTxnId)) || null : findUnpaidEndorsementTxn(txnRes?.transactions);
        if (endo) {
          // Endorsement mode — collect the endorsement delta, term unchanged.
          setEndorsementTxn(endo);
          setPremium(endo.premium_delta != null ? String(endo.premium_delta) : '');
          setEstimatedTaxes(endo.tax_delta != null ? String(endo.tax_delta) : '0');
          setEffective(endo.effective_date || start);
          setExpiration(end); // read-only context — existing term end
          const summary = String(endo.description || '').split('—')[0].trim() || 'Policy endorsement';
          const endoTotal = Math.round((Number(endo.premium_delta || 0) + Number(endo.tax_delta || 0) + Number(d.fees?.standard ?? 0)) * 100) / 100;
          const fmtEndoTotal = '$' + endoTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          setAutoSubject(carrier ? (amt) => `Endorsement payment: ${carrier}, ${amt}` : null, fmtEndoTotal);
          setWhatYourePayingFor(
            carrier
              ? `Mid-term endorsement on your ${lineNoun} policy with ${carrier}: ${summary}. Your existing coverage and term are unchanged.`
              : ''
          );
          return;
        }

        // Guard (a): no live bind on the ledger, so ask the server which quote the send would bind (txnRes unread: ask too).
        const hasLiveBind = (txnRes?.transactions || []).some((t) => t.type === 'bind' && !t.voided_at);
        if (!txnRes || !hasLiveBind) {
          api.get(`/api/session/admin/payment-request-bind/${encodeURIComponent(dealId)}${d.premium ? `?premium=${encodeURIComponent(d.premium)}` : ''}`)
            .then((p) => { setBindPlan(p); setBindQuoteId(p && p.chosen ? String(p.chosen.quote_id) : ''); })
            .catch(() => setBindPlan({ failed: true }));
        }

        // Renewal mode (unchanged)
        setPremium(d.premium ? String(d.premium) : '');
        setEstimatedTaxes('0');
        setEffective(start);
        setExpiration(end);
        const prem = Number(d.premium || 0);
        // The deal's fees as the server charges them (deal.fees, services/deal-fees.service).
        const dealFeeTotal = d.fees?.ok ? Number(d.fees.line?.amount || 0) + Number(d.fees.agency_line?.amount || 0) : 0;
        const defaultTotal = Math.round((prem + dealFeeTotal) * 100) / 100;
        const fmtTotal = '$' + defaultTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        setAutoSubject(carrier ? (amt) => `Payment request - ${carrier} - ${amt}` : null, fmtTotal);
        if (prefill?.whatYourePayingFor) {
          setWhatYourePayingFor(prefill.whatYourePayingFor);
        } else if (carrier && start && end && kind === 'builders_risk') {
          // A builders risk bind is signed on the Tower first; this link only
          // collects the premium. Never "renewal", never "same coverage".
          setWhatYourePayingFor(
            `Builders risk policy with ${carrier}${brProject ? ` covering ${brProject}` : ''}, for the period ${fmtD(start)} – ${fmtD(end)}. Your binding application is already signed; this request collects the premium.`
          );
        } else if (carrier && start && end && isRenewal) {
          // No-app bind hand-off: the broker's summary of changes replaces the
          // generic "same coverage" middle sentence.
          const middle = prefill?.changesSummary || 'Same coverage as the expiring policy.';
          setWhatYourePayingFor(
            `Renewal of your ${lineNoun} policy with ${carrier} for the period ${fmtD(start)} – ${fmtD(end)}. ${middle} No signature required.`
          );
        } else if (carrier && start && end) {
          // New business: say so, and claim nothing about an expiring policy
          // that does not exist.
          const middle = prefill?.changesSummary ? ` ${prefill.changesSummary}` : '';
          setWhatYourePayingFor(
            `Your new ${lineNoun} policy with ${carrier} for the period ${fmtD(start)} – ${fmtD(end)}.${middle}`
          );
        } else if (prefill?.changesSummary) {
          setWhatYourePayingFor(prefill.changesSummary);
        } else {
          setWhatYourePayingFor('');
        }
      })
      .catch(() => toast.error('Could not load deal details'))
      .finally(() => setLoading(false));
  }, [open, dealId, reloadKey, collectTxnId]);
  useEffect(() => { if (!open) setCollectTxnId(null); }, [open]);

  // Renewal only, and re-fetched when the effective date moves, because "does
  // this item ride THIS renewal" is asked as of the new term's start, not today.
  useEffect(() => {
    if (!open || !dealId || endorsementTxn) { setDeferredItems([]); setDeferredFailed(false); setDeferredSentence(null); return; }
    let cancelled = false;
    setDeferredFailed(false);
    getDeferredCollections(dealId, effective || undefined)
      .then((r) => {
        if (cancelled) return;
        setDeferredItems(r?.items || []);
        // The server words the state once; this renders it.
        setDeferredSentence(r?.sentence || null);
      })
      .catch(() => { if (!cancelled) { setDeferredItems([]); setDeferredSentence(null); setDeferredFailed(true); } });
    return () => { cancelled = true; };
  }, [open, dealId, effective, endorsementTxn, reloadKey]);

  // THE DEAL'S FEES (David 2026-10-27), read from deal.fees (the server's one
  // answer, services/deal-fees.service): a bind collection charges the deal's
  // platform fee (standard, reduced or WAIVED) plus any agency fee, and the server
  // charges exactly that whatever this sheet sends. An endorsement keeps the
  // standard platform fee. Unreadable fees: the preview says so and Send waits.
  const dealFees = deal?.fees && deal.fees.ok === true ? deal.fees : null;
  const feesUnknown = !dealFees;
  const standardFee = dealFees ? dealFees.standard : null;
  const endoPlatformNum = endoPlatform.trim() === '' ? standardFee : Number(endoPlatform);
  const endoAgencyNum = endoAgency.trim() === '' ? 0 : Number(endoAgency);
  const endoPlatformOk = endoPlatformNum != null && Number.isFinite(endoPlatformNum) && endoPlatformNum >= 0 && endoPlatformNum <= standardFee;
  const endoAgencyOk = Number.isFinite(endoAgencyNum) && endoAgencyNum >= 0;
  const endoNeedsReason = endoPlatformOk && endoPlatformNum < standardFee;
  const platformFee = dealFees ? (endorsementTxn ? (endoPlatformOk ? endoPlatformNum : 0) : Number(dealFees.line?.amount ?? dealFees.standard)) : 0;
  const agencyFee = dealFees ? (endorsementTxn ? (endoAgencyOk ? endoAgencyNum : 0) : Number(dealFees.agency_line?.amount || 0)) : 0;
  const platformWord = endorsementTxn
    ? (endoPlatformOk && endoPlatformNum === 0 ? 'waived' : null)
    : (dealFees?.line?.display ? dealFees.line.display.toLowerCase() : null);
  const premiumNum = Number(premium) || 0;
  const taxNum = Number(estimatedTaxes) || 0;
  // ONE platform fee, however many obligations ride along. That is the point of
  // stacking them: the client pays one fee instead of one per invoice.
  // A deferral on THIS deal is this term's own obligation, never a line riding
  // its own invoice (that would bill it twice). It is collected by ending the
  // deferral, which makes it the unpaid row this request collects.
  const isOwnDeal = (d) => String(d.crm_deal_id || '') === String(dealId);
  const includedDeferred = deferredItems.filter((d) => d.collectible && !isOwnDeal(d) && !excludedDeferred.includes(d.transaction_id));
  const ownDealDeferredIds = deferredItems.filter(isOwnDeal).map((d) => d.transaction_id);
  const excludeOnSend = [...new Set([...excludedDeferred, ...ownDealDeferredIds])];

  async function handleEndDeferral(d) {
    setEndingDeferral(d.transaction_id);
    try {
      const res = await endDeferral(d.transaction_id);
      if (res?.success === false) throw new Error(res?.error || 'Could not end the deferral');
      // Verification is a read, not an echo.
      let row = null;
      try {
        const back = await getDealTransactions(dealId);
        row = (back?.transactions || []).find((t) => t.id === d.transaction_id) || null;
      } catch (_) { row = null; }
      if (!row) toast.info('Deferral ended, but the ledger could not be re-read to confirm. Reopen to check.');
      else if (row.ar_payment_status === 'deferred') toast.error('The ledger still reads deferred. The change did not land.');
      else toast.success(`${money2(d.amount)} is no longer deferred. It is the amount this request collects.`);
      setReloadKey((k) => k + 1);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Could not end the deferral');
    } finally {
      setEndingDeferral(null);
    }
  }
  const deferredTotal = Math.round(includedDeferred.reduce((sum, d) => sum + Number(d.amount || 0), 0) * 100) / 100;
  const carrierFee = endorsementTxn ? 0 : (carrierFeeDue || 0);
  const carrierFeeUnknown = !endorsementTxn && carrierFeeDue === null;
  const total = Math.round((premiumNum + taxNum + carrierFee + platformFee + agencyFee + deferredTotal) * 100) / 100;
  const selectedCredit = availableCredits.find((c) => c.id === selectedCreditId) || null;
  const creditApplied = selectedCredit ? Math.round(Number(selectedCredit.amount) * 100) / 100 : 0;
  const totalDue = Math.round((total - creditApplied) * 100) / 100;
  useEffect(() => {
    if (!subjectTpl.current || emailSubject !== autoSubject.current) return;
    const next = subjectTpl.current('$' + totalDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
    if (next === emailSubject) return;
    autoSubject.current = next;
    setEmailSubject(next);
  }, [totalDue]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSend() {
    if (!premiumNum || premiumNum <= 0) { toast.error('Premium must be > 0'); return; }
    if (feesUnknown) { toast.error('This deal\'s fees could not be read. Reopen the sheet and try again.'); return; }
    if (carrierFeeUnknown) { toast.error('This deal\'s carrier fee could not be read. Reopen the sheet and try again.'); return; }
    if (endorsementTxn && (!endoPlatformOk || !endoAgencyOk)) { toast.error(`Platform fee: $0 up to $${standardFee}; agency fee: an amount or blank.`); return; }
    if (endorsementTxn && endoNeedsReason && !endoReason.trim()) { toast.error('Say why the platform fee is reduced or waived.'); return; }
    // Kind-aware date guard: an endorsement needs only its effective date —
    // the policy term is unchanged. Renewal still needs both.
    if (endorsementTxn ? !effective : (!effective || !expiration)) {
      toast.error(endorsementTxn ? 'Endorsement effective date is required' : 'Policy dates are required');
      return;
    }
    if (!whatYourePayingFor.trim()) { toast.error('Description is required'); return; }
    if (selectedCreditId && !endorsementTxn && !(totalDue > 0)) {
      toast.error('Credit meets or exceeds the total. Use a smaller credit — a zero-collect credit is not supported.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await sendPaymentRequest(dealId, {
        amount_breakdown: {
          premium: premiumNum, estimated_taxes: taxNum, platform_fee: platformFee,
          ...(endorsementTxn ? { platform_fee_reason: endoNeedsReason ? endoReason.trim() : null, agency_fee: agencyFee > 0 ? agencyFee : null } : {}),
        },
        // Endorsement submits its effective date only — no policy_period that
        // implies a new term. The server defaults/derives the rest.
        policy_period: endorsementTxn ? { effective } : { effective, expiration },
        ...(endorsementTxn ? { endorsement_transaction_id: endorsementTxn.id } : {}),
        policy_number: policyNumber || undefined,
        what_youre_paying_for: whatYourePayingFor.trim(),
        // No-app renewal bind: summary of changes rides into the client
        // acknowledgment text (renewal kind only; server ignores otherwise).
        ...(!endorsementTxn && prefill?.changesSummary ? { changes_summary: prefill.changesSummary } : {}),
        ...(selectedCreditId && !endorsementTxn ? { credit_id: selectedCreditId } : {}),
        // Guard (a): the quote the bind is recorded from when the deal has none on the ledger.
        ...(bindWillRecord ? { quote_id: bindQuoteId } : {}),
        // Only what David struck off. The server recomputes the lines itself;
        // it never takes an amount from here.
        ...(excludeOnSend.length ? { exclude_deferred_ids: excludeOnSend } : {}),
        broker_message: brokerMessage.trim() || undefined,
        email_subject: emailSubject.trim() || undefined,
        broker_email: localStorage.getItem('tracon_email') || 'broker',
      });
      if (!res?.success) {
        toast.error(res?.error || 'Failed to send payment request');
        return;
      }
      setResult(res);
      setRefusal(null);
      toast.success('Payment request sent');
      for (const w of res.warnings || []) toast.info(w);
      if (typeof onChange === 'function') onChange();
    } catch (err) {
      if (isHoldRefusal(err)) {
        // A paperwork hold (hold mode): nothing was created or sent. The block above says why, with its button.
        setRefusal(err.body);
        setReloadHolds((k) => k + 1);
        return;
      }
      if (err instanceof ApiError && err.status === 409 && (err.body?.code === 'BIND_QUOTE_NEEDED' || err.body?.code === 'NO_QUOTE_TO_BIND')) {
        // Not a refusal: the server could not tell which quote this request binds. The question is asked above.
        setBindPlan({ live: false, ask: err.body.code === 'BIND_QUOTE_NEEDED', none: err.body.code === 'NO_QUOTE_TO_BIND', why: err.body.error, candidates: err.body.candidates || [] });
        setBindQuoteId('');
        toast.info(err.body.error);
        return;
      }
      if (err instanceof ApiError && err.status === 409 && err.body?.code === 'BIND_RECORDED_FEE_CHANGED') {
        // The bind is recorded; the carrier's fee it carries was not in this total. Read the deal again so Send bills it.
        toast.info(err.body.error);
        setReloadKey((k) => k + 1);
        return;
      }
      if (err instanceof ApiError && err.status === 409 && err.body?.existing_token) {
        // Not a dead end: the open request is listed at the top of this sheet
        // with its own reminder, resend and cancel.
        toast.error('This deal already has an open payment request. Send a reminder on it above, or cancel it to send a new one.');
        setReloadOpen((k) => k + 1);
        return;
      }
      const msg = err instanceof ApiError ? err.body?.error || err.code : err.message || 'request failed';
      toast.error(`Couldn't send payment request: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  }

  function copyLink() {
    if (!result?.access_url) return;
    navigator.clipboard.writeText(result.access_url)
      .then(() => toast.success('Link copied'))
      .catch(() => toast.error('Copy failed'));
  }

  function handleClose(v) {
    onOpenChange?.(v);
    if (!v) setResult(null);
  }

  const contactEmail = deal?.primary_contact?.email || '';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      {/* L2-2 Chrome pass: the sheet (with a hold block) ran taller than the window, its title and Send button off screen; it scrolls inside. */}
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {result ? (
              <><CheckCircle2 size={18} className="text-success" /> {endorsementTxn ? 'Endorsement payment request sent' : 'Payment request sent'}</>
            ) : (
              <><CreditCard size={18} className="text-accent" /> {endorsementTxn ? 'Send endorsement payment request' : 'Send payment request'}</>
            )}
          </DialogTitle>
          <DialogDescription>
            {result
              ? <>Email sent to <strong className="text-text">{result.email_sent ? contactEmail || 'the client' : 'N/A'}</strong>. You can also share the link directly.</>
              : <>Create a payment link for this deal. The client will review, acknowledge, and pay online.</>
            }
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <OpenRequests
            requests={openRequests}
            failed={openFailed}
            dealId={dealId}
            onSent={() => setReloadOpen((k) => k + 1)}
            onCancelled={() => { setReloadOpen((k) => k + 1); if (typeof onChange === 'function') onChange(); }}
          />
        )}
        {result ? (
          <>
            {result.access_url && (
              <div className="my-2 rounded-card border border-border bg-bg p-3">
                <div className="break-all font-mono text-xs text-text">{result.access_url}</div>
              </div>
            )}
            <div className="text-sm text-text-muted">
              Total: <strong className="text-text">${total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
            </div>
          </>
        ) : loading ? (
          <div className="py-8 text-center text-text-muted text-sm">Loading deal data...</div>
        ) : (
          <div className="space-y-4 my-2">
            {carrierBlocks && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                This deal has no carrier yet, so the request has no one to name. Record the bind from its quote (the quote carries the carrier), or set the carrier with Edit policy info, then send.
              </div>
            )}
            {/* GUARD (a) (David 2026-11-03): no live bind on the ledger, so sending records it first, from the quote named here.
                The server's own answer (GET /api/session/admin/payment-request-bind), never a guess made in this sheet. */}
            {!endorsementTxn && bindPlan && bindPlan.failed && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="pr-bind-plan-failed">
                Could not check this deal's ledger for a bind. Close the sheet and open it again before sending.
              </div>
            )}
            {!endorsementTxn && bindPlan && !bindPlan.failed && !bindPlan.live && (
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800 space-y-2" data-testid="pr-bind-plan">
                {bindPlan.none ? (
                  <p>{bindPlan.why}</p>
                ) : (
                  <>
                    <p>This deal has no bind on the ledger yet. Sending records the bind from this quote first, then sends the request against it.</p>
                    <select
                      aria-label="The quote this request binds"
                      className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
                      value={bindQuoteId}
                      onChange={(e) => setBindQuoteId(e.target.value)}
                    >
                      <option value="">Choose the quote</option>
                      {(bindPlan.candidates || []).map((c) => (
                        <option key={c.quote_id} value={c.quote_id}>{`${c.carrier || 'Carrier not stated'}: ${c.premium != null ? money2(c.premium) : 'no premium on the quote'}`}</option>
                      ))}
                    </select>
                    {bindPlan.ask && !bindQuoteId && <p>{bindPlan.why}</p>}
                    {bindQuote && bindQuote.premium != null && premiumNum > 0 && Math.abs(Number(bindQuote.premium) - premiumNum) >= 0.005 && (
                      <p>This request's premium ({money2(premiumNum)}) is not the quote's ({money2(bindQuote.premium)}). The bind records the quote's premium.</p>
                    )}
                  </>
                )}
              </div>
            )}
            {endorsementTxn && (
              <div className="rounded-md border border-sky-300 bg-sky-50 p-3 text-sm text-sky-800">
                Collecting an unpaid <strong>endorsement</strong>: {String(endorsementTxn.description || '').split('—')[0].trim() || 'Policy endorsement'}.
                The policy term and deal amount are not changed by this request.
              </div>
            )}
            {holdsFailed && (
              <p className="text-xs text-danger" data-holds-unread>Could not check the paperwork holds for this deal. The server still checks them when you send.</p>
            )}
            {(() => {
              // The server's findings for THIS request: the endorsement's own (endorsement mode) or the renewal's; a refusal's
              // findings when the send was refused for a kind the sheet was not showing (a premium that no longer matched).
              const policyLabel = holds?.signed_application?.policy?.label || deal?.deal_name || 'this policy';
              const shown = refusal?.holds?.length ? refusal.holds
                : !holds ? [] : endorsementTxn ? (holds.payment_request?.endorsement?.[endorsementTxn.id] || []) : (holds.payment_request?.renewal || []);
              const mode = refusal?.mode || holds?.mode;
              return shown.map((f) => (
                f.kind === 'signed_application'
                  ? <SignedApplicationHold key={f.kind} finding={f} mode={mode} door="payment" dealId={dealId} policyLabel={policyLabel} canWaive={!!holds?.can_waive} canWaiveWhy={holds?.can_waive_why} onDrop={() => setAppDropOpen(true)} onChanged={holdsChanged} />
                  : f.kind === 'endorsement_document'
                    ? <EndorsementDocumentHold key={f.kind} finding={f} mode={mode} dealId={dealId} onChanged={holdsChanged} />
                    : <UnmatchedEndorsementHold key={f.kind} finding={f} mode={mode} onCollect={collectEndorsement} />
              ));
            })()}
            {!endorsementTxn && ['BOUND', 'RENEWAL_DUE'].includes(deal?.stage) && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                This term shows no unpaid endorsement on the ledger. This will invoice the full term premium shown below. Double-check before sending.
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-text-muted block mb-1">Email subject</label>
              <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Payment request - Carrier - $0.00" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text-muted block mb-1">Premium ($)</label>
                <Input type="number" step="0.01" min="0" value={premium} onChange={(e) => setPremium(e.target.value)} placeholder="8400.00" />
              </div>
              <div>
                <label className="text-xs font-medium text-text-muted block mb-1">Est. taxes ($)</label>
                <Input type="number" step="0.01" min="0" value={estimatedTaxes} onChange={(e) => setEstimatedTaxes(e.target.value)} placeholder="0" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-text-muted block mb-1">
                  {endorsementTxn ? 'Endorsement effective date' : 'Effective date'}
                </label>
                <Input type="date" value={effective} onChange={(e) => setEffective(e.target.value)} />
              </div>
              {endorsementTxn ? (
                <div>
                  <label className="text-xs font-medium text-text-muted block mb-1">Policy term (unchanged)</label>
                  <div className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-text-muted">
                    {deal?.policy_start ? fmtD(deal.policy_start.split('T')[0]) : '—'} – {deal?.policy_expiration ? fmtD(deal.policy_expiration.split('T')[0]) : '—'}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="text-xs font-medium text-text-muted block mb-1">Expiration date</label>
                  <Input type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />
                </div>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted block mb-1">Policy number (optional)</label>
              <Input value={policyNumber} onChange={(e) => setPolicyNumber(e.target.value)} placeholder="AVN-12345-26" />
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted block mb-1">Description (shown to client)</label>
              <textarea
                className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
                rows={3}
                value={whatYourePayingFor}
                onChange={(e) => setWhatYourePayingFor(e.target.value)}
                placeholder="What the client is paying for..."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-text-muted block mb-1">Broker note (optional, shown below description)</label>
              <Input value={brokerMessage} onChange={(e) => setBrokerMessage(e.target.value)} placeholder="Optional note to client" />
            </div>
            {endorsementTxn && dealFees && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-xs font-medium text-text-dim">Fees on this endorsement request</p>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs text-text-muted">
                    Platform fee (standard {money2(standardFee)}; 0 waives it)
                    <Input inputMode="decimal" value={endoPlatform} placeholder={String(standardFee)} onChange={(e) => setEndoPlatform(e.target.value.replace(/[^0-9.]/g, ''))} className="mt-1" />
                  </label>
                  <label className="text-xs text-text-muted">
                    Agency fee (blank for none)
                    <Input inputMode="decimal" value={endoAgency} placeholder="none" onChange={(e) => setEndoAgency(e.target.value.replace(/[^0-9.]/g, ''))} className="mt-1" />
                  </label>
                </div>
                {endoNeedsReason && (
                  <label className="block text-xs text-text-muted">
                    Reason (yours, never shown to the client)
                    <Input value={endoReason} onChange={(e) => setEndoReason(e.target.value)} className="mt-1" />
                  </label>
                )}
                {(!endoPlatformOk || !endoAgencyOk) && (
                  <p className="text-xs text-danger">Platform fee: from $0 up to {money2(standardFee)}. Agency fee: an amount, or blank for none.</p>
                )}
              </div>
            )}
            {!endorsementTxn && availableCredits.length > 0 && (
              <div className="border-t border-border pt-3">
                <label className="text-xs font-medium text-text-muted block mb-1">Apply account credit (optional)</label>
                <select
                  className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-text focus:outline-none focus:ring-1 focus:ring-accent"
                  value={selectedCreditId}
                  onChange={(e) => setSelectedCreditId(e.target.value)}
                >
                  <option value="">No credit</option>
                  {availableCredits.map((c) => (
                    <option key={c.id} value={c.id}>
                      {money2(c.amount)} from {c.source_account_id ? 'account' : 'deal'} {c.source_crm_deal_id}{c.memo ? ` — ${c.memo}` : ''}
                    </option>
                  ))}
                </select>
                {selectedCredit && (
                  <p className="mt-1 text-[11px] text-amber-600">
                    Applying {money2(selectedCredit.amount)} from source deal {selectedCredit.source_crm_deal_id}
                    {selectedCredit.via === 'rewrite' ? ', admitted because this deal is a rewrite of that policy.' : ', held on this account.'}
                  </p>
                )}
              </div>
            )}
            {/* Deferred items riding this renewal (184 §4). Shown BEFORE
                sending, with a per-line opt out, because nothing bills anyone
                without David releasing it and a line he cannot see is a line
                he cannot decline. Items that are not yet due are listed too,
                greyed and unchecked, so "not due" and "you struck it off" stay
                visibly different facts. */}
            {deferredFailed && (
              <p className="border-t border-border pt-3 text-xs text-danger">
                Could not check this account for deferred items. This invoice may be missing money.
                Retry before sending.
              </p>
            )}
            {!deferredFailed && deferredItems.length === 0 && deferredSentence === null && (
              <p className="border-t border-border pt-3 text-xs text-text-dim">
                Nothing deferred on this account.
              </p>
            )}
            {deferredItems.length > 0 && (
              <div className="border-t border-border pt-3 space-y-2">
                <p className="text-xs font-medium text-text-dim">
                  {deferredSentence || 'Outstanding on this account, riding this invoice as its own line'}
                </p>
                {deferredItems.map((d) => {
                  const excluded = excludedDeferred.includes(d.transaction_id);
                  if (isOwnDeal(d)) {
                    return (
                      <div key={d.transaction_id} className="flex items-start gap-2 text-xs text-text">
                        <span className="flex-1">
                          {d.label} <strong>{money2(d.amount)}</strong>
                          <span className="text-text-dim"> · deferred on this policy{d.reason ? `: ${d.reason}` : ''}</span>
                        </span>
                        <Button size="sm" variant="outline" disabled={!!endingDeferral} onClick={() => handleEndDeferral(d)}>
                          {endingDeferral === d.transaction_id ? 'Ending deferral...' : 'Collect it on this invoice'}
                        </Button>
                      </div>
                    );
                  }
                  return (
                    <label key={d.transaction_id} className={`flex items-start gap-2 text-xs ${d.collectible ? 'text-text' : 'text-text-dim'}`}>
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        disabled={!d.collectible}
                        checked={d.collectible && !excluded}
                        onChange={(e) => setExcludedDeferred((prev) => (
                          e.target.checked ? prev.filter((id) => id !== d.transaction_id) : [...prev, d.transaction_id]
                        ))}
                      />
                      <span className="flex-1">
                        {d.label} <strong>{money2(d.amount)}</strong>
                        {!d.collectible && (
                          <em className="not-italic text-text-dim">
                            {' '}(not due yet{d.collect_effective ? `, ${d.collect_effective}` : ''}, so it is not on this invoice)
                          </em>
                        )}
                        {d.crm_deal_id && d.crm_deal_id !== String(dealId) && (
                          <span className="text-text-dim"> · from the expiring policy</span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="text-sm text-text-muted border-t border-border pt-3">
              Premium {money2(premiumNum)} + taxes {money2(taxNum)}
              {carrierFeeUnknown ? <> + carrier fee (could not be read)</> : carrierFee > 0 && <> + carrier program fee {money2(carrierFee)}</>}
              {deferredTotal > 0 && <> + outstanding {money2(deferredTotal)}</>}
              {feesUnknown
                ? <> + fees (could not be read)</>
                : <>{' '}+ platform fee {platformWord || money2(platformFee)}{agencyFee > 0 && <> + agency fee {money2(agencyFee)}</>}</>}
              {' '}= {money2(total)}
              {creditApplied > 0 && (
                <> − credit {money2(creditApplied)}</>
              )}
              {' '}= <strong className="text-text">{money2(totalDue)}</strong> total due
            </div>
          </div>
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)}>Done</Button>
              <Button type="button" onClick={copyLink}><Copy size={14} strokeWidth={1.5} /> Copy link</Button>
            </>
          ) : (
            <>
              <Button type="button" variant="ghost" onClick={() => handleClose(false)} disabled={submitting}>Cancel</Button>
              <Button type="button" onClick={handleSend} disabled={submitting || loading || carrierBlocks || heldNow || bindNeedsChoice} data-action="send_payment_request">
                {submitting ? 'Sending...' : heldNow ? 'Held: resolve the paperwork above' : bindNeedsChoice ? (bindPlan && bindPlan.none ? 'No quote to bind: log the quote first' : 'Choose the quote this request binds') : bindWillRecord ? `Record the bind and send the request (${money2(totalDue)})` : `Send request (${money2(totalDue)})`}
              </Button>
            </>
          )}
        </DialogFooter>
        <PolicyDocsDropSheet dealId={dealId} open={appDropOpen} onOpenChange={setAppDropOpen} initialKind="application" onSuccess={() => { setAppDropOpen(false); holdsChanged(); }} />
      </DialogContent>
    </Dialog>
  );
}
